import React, { useMemo } from 'react';
import { Box, Typography, Tooltip, CircularProgress } from '@mui/material';

/**
 * Timeline de mudanças: uma coluna por dia.
 *
 * Duas faixas, de propósito separadas:
 *
 *   em cima  volume de mudanças SEMÂNTICAS, empilhado pelas mesmas categorias
 *            e cores da barra que já existia. É o que a rede passou a fazer
 *            de diferente.
 *   embaixo  linhas de config que entraram e saíram. É o volume de TEXTO.
 *
 * Manter as duas é o ponto: um dia pode ter 79 linhas novas e zero mudança
 * semântica — é configuração que o parser ainda não lê, e some se a gente
 * mostrar só o de cima. Um dia com 13 mudanças e 10 linhas é o contrário:
 * pouca digitação, muito efeito.
 *
 * Cada barra é escalada pelo maior valor da própria faixa. Escala comum entre
 * as duas achataria a de cima, porque contagem de linha é uma ordem de
 * grandeza maior que contagem de mudança.
 */

const ALT_MUD = 84;    // altura da faixa de mudanças
const ALT_LIN = 26;    // altura da faixa de linhas
const LARG = 22;       // largura da coluna

export default function Timeline({ barras, carregando, diaSel, onSelecionarDia, cores }) {
  const { maxMud, maxLin } = useMemo(() => {
    let m = 1, l = 1;
    for (const b of barras || []) {
      if (b.total_changes > m) m = b.total_changes;
      const t = (b.linhas_mais || 0) + (b.linhas_menos || 0);
      if (t > l) l = t;
    }
    return { maxMud: m, maxLin: l };
  }, [barras]);

  if (carregando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (!barras || !barras.length) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>
        Clique num dia para ver o que mudou nele
      </Typography>
      <Box sx={{
        display: 'flex', alignItems: 'flex-end', gap: '3px',
        overflowX: 'auto', pb: 1,
        '&::-webkit-scrollbar': { height: 6 },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 },
      }}>
        {barras.map((b) => {
          const sel = diaSel === b.dia;
          const cats = Object.entries(b.by_category || {}).sort((x, y) => y[1] - x[1]);
          const lin = (b.linhas_mais || 0) + (b.linhas_menos || 0);
          const hMais = Math.round((b.linhas_mais || 0) / maxLin * ALT_LIN);
          const hMenos = Math.round((b.linhas_menos || 0) / maxLin * ALT_LIN);
          const dica = (
            <Box sx={{ fontSize: 12 }}>
              <b>{b.dia}</b><br />
              {b.total_changes} mudanças em {b.hosts_changed} equipamentos<br />
              {b.hosts_texto ? `${b.hosts_texto} só mudaram texto` : ''}
              {b.hosts_texto ? <br /> : null}
              +{b.linhas_mais} −{b.linhas_menos} linhas
              {cats.length ? <br /> : null}
              {cats.map(([k, v]) => `${k} ${v}`).join(' · ')}
            </Box>
          );
          return (
            <Tooltip key={b.dia} title={dica} placement="top">
              <Box
                onClick={() => onSelecionarDia(sel ? null : b.dia)}
                sx={{
                  width: LARG, flexShrink: 0, cursor: 'pointer',
                  borderRadius: 0.5, px: '2px', pt: 0.5,
                  bgcolor: sel ? '#e2e8f0' : 'transparent',
                  '&:hover': { bgcolor: '#f1f5f9' },
                }}
              >
                {/* faixa de mudanças, empilhada por categoria */}
                <Box sx={{
                  height: ALT_MUD, display: 'flex', flexDirection: 'column',
                  justifyContent: 'flex-end',
                }}>
                  {cats.map(([k, v]) => (
                    <Box key={k} sx={{
                      height: Math.max(2, Math.round((v / maxMud) * ALT_MUD)),
                      bgcolor: (cores && cores[k]) || '#94a3b8',
                    }} />
                  ))}
                  {/* dia sem mudança semântica ainda merece um traço: senão
                      some da timeline e parece que não houve coleta */}
                  {!cats.length && (
                    <Box sx={{ height: 2, bgcolor: '#e2e8f0' }} />
                  )}
                </Box>

                {/* faixa de linhas: entram pra cima, saem pra baixo */}
                <Box sx={{
                  height: ALT_LIN, display: 'flex', flexDirection: 'column',
                  justifyContent: 'center', mt: '2px',
                  borderTop: '1px solid #e2e8f0',
                }}>
                  <Box sx={{ height: hMais, bgcolor: '#86efac' }} />
                  <Box sx={{ height: hMenos, bgcolor: '#fca5a5' }} />
                </Box>

                <Typography sx={{
                  fontSize: 8.5, textAlign: 'center', mt: 0.3,
                  color: sel ? '#0f172a' : '#94a3b8',
                  fontWeight: sel ? 700 : 400,
                  whiteSpace: 'nowrap',
                }}>
                  {b.dia.slice(8)}/{b.dia.slice(5, 7)}
                </Typography>
                {lin === 0 && !cats.length ? null : null}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}
